import { AddAPhotoOutlined, Close, Delete, Download, Save } from "@mui/icons-material"
import { Grid, IconButton, Tooltip, Typography } from "@mui/material"
import { clsx } from "clsx"
import { ClipboardEvent, useRef, useState } from "react"
import { useGtXs } from "../../hooks/useGtXs"
import useToast from "../../hooks/useToast"
import config from "../../services/config"
import { formatPhotoFormatsEnv, staticFile } from "../../services/utils"
import { GalleryImageProps } from "../../types/types"
import ConfirmButton from "../ConfirmButton/ConfirmButton"
import Show from "../Show/Show"
import Truncate from "../Truncate/Truncate"
import styles from './GalleryImage.module.scss'

export default function GalleryImage(props: GalleryImageProps) {
    const { image,
        onDelete,
        onUpdate,
        onDownload,
        disableDelete = false,
        disableDownload = false,
        isDragActive = false,
        disableUpdate = false,
        getInputProps,
        getRootProps = {}
    } = props
    const [isEditable, setIsEditable] = useState(false);
    const ref = useRef<HTMLDivElement>(null)
    const { errorToast } = useToast()
    const gtXs = useGtXs()

    const clickToEditMessage = "Descrição"

    const formats = formatPhotoFormatsEnv(config.ALLOWED_PHOTO_FORMATS)
    const classes = clsx({ [styles.GalleryImagePlaceholder]: true, [styles.DragActive]: isDragActive })

    if (!image) {
        return (
            <Grid container direction="column" justifyContent="center" alignItems="center" className={classes} {...getRootProps()}>
                <input {...getInputProps()} />
                <AddAPhotoOutlined className={styles.AddPhoto} color="primary" />
                <Show on={!gtXs}>
                    <Typography variant="h5" color="primary">
                        Clique para adicionar fotos.
                    </Typography>
                </Show>
                <Show on={gtXs}>
                    <Typography variant="h5" color="primary">
                        Arraste ou clique para adicionar fotos.
                    </Typography>
                    <Typography variant="h5" color="primary">
                        {formats}
                    </Typography>
                </Show>
            </Grid>
        )
    }

    if (!onDelete || !onDownload) {
        return null
    }

    const handleSetIsEditable = () => {
        if (disableUpdate) return

        setIsEditable(true)

        setTimeout(() => {
            if (ref.current) {
                const isPlaceholder = ref.current.innerText === clickToEditMessage

                if (isPlaceholder) {
                    ref.current.innerText = ""
                }

                ref.current.focus()

                const range = document.createRange()
                const sel = window.getSelection()

                if (ref.current.childNodes.length > 0) {
                    const textNode = ref.current.childNodes[0]
                    const position = isPlaceholder ? 0 : textNode.textContent?.length || 0
                    range.setStart(textNode, position)
                    range.setEnd(textNode, position)
                } else {
                    range.selectNodeContents(ref.current)
                    range.collapse(false)
                }

                sel?.removeAllRanges()
                sel?.addRange(range)
            }
        }, 10)
    }

    const handleUpdate = () => {
        const description = ref.current?.innerText?.trim()

        if (!description || description.length <= 1) {
            if (ref.current) {
                ref.current.innerText = clickToEditMessage
            }
            setIsEditable(false)
            return
        }

        if (description !== image.description) {
            onUpdate(image?.id, { description })
        }
        setIsEditable(false)
    }

    const handlePaste = (event: ClipboardEvent) => {
        event.preventDefault()
        const text = event.clipboardData.getData('text/plain')
        if (ref && ref.current) {
            ref.current.innerText = text
        } else {
            errorToast("Erro ao colar texto, tente novamente por favor.")
        }
    }

    const handleCancel = () => {
        setIsEditable(false)
        if (ref?.current && image.description) {
            ref.current.innerText = image.description
        } else if (ref?.current) {
            ref.current.innerText = clickToEditMessage
        }
    }

    const hasDescription = image?.description && image?.description?.length > 1
    const descriptionClasses = clsx({ [styles.DescriptionWrapper]: true, [styles.IsEditable]: !disableUpdate && !isEditable })

    return (
        <div className={styles.Wrapper}>
            <div className={styles.GalleryImage}>
                <img src={staticFile(image.thumbnail)} className={styles.Image} alt={image.name} />
                <Grid container justifyContent="space-between" alignItems="center">
                    <div style={{ maxWidth: "80%" }}>
                        <Typography variant="body1" color="secondary">
                            <Truncate text={gtXs ? image.name : ""} />
                        </Typography>
                    </div>
                    <div>
                        {!disableDelete && <ConfirmButton onClick={() => onDelete(image)} IconButtonProps={{ size: "small", edge: "end" }}>
                            <Delete color="primary" />
                        </ConfirmButton>}

                        <Tooltip title="Descarregar">
                            <IconButton onClick={(event: any) => { event.stopPropagation(); onDownload(image) }} size="small" style={{ marginTop: 5 }} edge="end" disabled={disableDownload}>
                                <Download color="primary" />
                            </IconButton>
                        </Tooltip>

                    </div>
                </Grid>
            </div>
            <div className={descriptionClasses}>
                <div
                    ref={ref}
                    lang="pt"
                    contentEditable={!disableUpdate && isEditable}
                    className={styles.Description}
                    onClick={handleSetIsEditable}
                    onPaste={handlePaste}
                    suppressContentEditableWarning={true}
                    style={{
                        color: hasDescription ? 'inherit' : 'rgba(0, 0, 0, 0.6)',
                        fontSize: '0.875rem',
                        minHeight: '20px',
                        padding: '4px',
                        outline: isEditable ? '1px solid rgba(0, 0, 0, 0.23)' : 'none',
                        borderRadius: '4px',
                        cursor: !disableUpdate ? 'text' : 'default'
                    }}
                >
                    {hasDescription ? image.description : clickToEditMessage}
                </div>
                <Grid container justifyContent="flex-end">
                    <Show on={!disableUpdate && isEditable}>
                        <ConfirmButton
                            onClick={handleCancel}
                            message="Quer terminar edição da descrição. Pode perder alterações não guardadas."
                            tooltip="Cancelar"
                        >
                            <Close fontSize="small" />
                        </ConfirmButton>
                    </Show>
                    <Show on={!disableUpdate && isEditable}>
                        <Tooltip title="Guardar">
                            <IconButton onClick={handleUpdate} style={{ marginRight: -10 }}>
                                <Save fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Show>
                </Grid>
            </div>
        </div>
    )
}
